<template>
  <div class="rate-card-wrapper">
    <RateTypeCard
      v-if="rates?.mobile"
      :amount="rates.mobile"
      :hasMultipleRates="singleRate?.hasMultipleRates || rates.mobile.hasMultipleRates"
      :title="singleRate?.name"
      :ratePerMinute="rates.mobile.minutes"
      type="mobile"
    />
    <div class="divider" v-if="rates?.mobile && rates?.landline"></div>
    <RateTypeCard
      v-if="rates?.landline"
      :amount="rates.landline"
      :isMobile="false"
      :hasMultipleRates="singleRate?.hasMultipleRates || rates.landline.hasMultipleRates"
      :title="singleRate?.name"
      :ratePerMinute="rates.landline.minutes"
      type="landline"
    />

    <RateTypeCard
      v-if="singleRate && rates?.premium"
      :amount="rates?.premium"
      :isMobile="false"
      :hasMultipleRates="singleRate.hasMultipleRates"
      :title="singleRate?.name"
      :ratePerMinute="rates?.premium?.minutes"
      type="premium"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import RateTypeCard from '@/components/RateTypeCard.vue';
import { formatCurrency } from '@/utils/helpers';
import { CountryRate, RateListItem } from '@/types/rates';

export default defineComponent({
  name: 'RateCard',
  components: { RateTypeCard },
  props: {
    selectedCountryRates: Object as PropType<CountryRate | null>,
    singleRate: Object as PropType<RateListItem | null>,
    currency: String,
    isBuyCredit: Boolean,
    selectedAmount: Number,
    extra: Object as PropType<number | null>,
  },
  setup(props) {
    const i18n = useI18n();
    const { t } = i18n;

    const rates = computed(() => {
      const mobileRates = props.selectedCountryRates?.mobile ?? [];
      const landlineRates = props.selectedCountryRates?.landline ?? [];
      const premiumRates = props.selectedCountryRates?.premium ?? [];

      const mobileRate = mobileRates.length ? Math.min(...mobileRates.map(({ rate }) => rate)) : 0;
      const landlineRate = landlineRates.length ? Math.min(...landlineRates.map(({ rate }) => rate)) : 0;
      const premiumRate = premiumRates.length ? Math.min(...premiumRates.map(({ rate }) => rate)) : 0;

      if (props.singleRate) {
        return {
          [props.singleRate.type]: {
            rate: props.singleRate.amount,
            unit: t('rateFinder.rate.unit'),
          },
        };
      }

      const calculatedRates: Record<string, any> = {};

      if (mobileRates.length > 0 && mobileRate > 0) {
        calculatedRates.mobile = {
          rate: formatCurrency(mobileRate, props.currency),
          minutes: props.selectedAmount
            ? Math.floor(((props.selectedAmount || 0) + (props.extra || 0)) / (mobileRate * 100))
            : undefined,
          hasMultipleRates: mobileRates.length > 1,
          unit: t('rateFinder.rate.unit'),
          type: 'mobile',
        };
      }

      if (landlineRates.length > 0 && landlineRate > 0) {
        calculatedRates.landline = {
          rate: formatCurrency(landlineRate, props.currency),
          minutes: props.selectedAmount
            ? Math.floor(((props.selectedAmount || 0) + (props.extra || 0)) / (landlineRate * 100))
            : undefined,
          hasMultipleRates: landlineRates.length > 1,
          unit: t('rateFinder.rate.unit'),
          type: 'landline',
        };
      }

      if (premiumRates.length > 0 && premiumRate > 0) {
        calculatedRates.premium = {
          rate: formatCurrency(premiumRate, props.currency),
          minutes: props.selectedAmount
            ? Math.floor(((props.selectedAmount || 0) + (props.extra || 0)) / (premiumRate * 100))
            : undefined,
          hasMultipleRates: premiumRates.length > 1,
          unit: t('rateFinder.rate.unit'),
          type: 'premium',
        };
      }

      return calculatedRates;
    });
    return {
      rates,
    };
  },
});
</script>

<style lang="scss" scoped>
.rate-card-wrapper {
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  p {
    margin: 0;
  }
  height: 105px;
}
.divider {
  border-left: 1px solid $color-wide-blue-yonder;
  height: 100%;
}
</style>
